import { Component, Element, Event, EventEmitter, h, Host, Listen, Prop, State } from "@stencil/core";
import { Label } from "../label/label";
import { debounce } from "../../utils/debounce";

let id = 0;

/**
 * @slot (default) - Label element slot, for label additional elements
 * @slot info-min - info label for the left side of the range
 * @slot info-max - info label for the right side of the range
 */

@Component({
    tag: "ux-range-input",
    styleUrl: "range-input.scss",
    shadow: false
})
export class RangeInput {
    private baseClass = "ux-range-input";
    private rangeInput: HTMLInputElement;
    private rangeMarker: HTMLDivElement;

    @Element() element!: HTMLUxRangeInputElement;

    /**
     * The minimum value that can be selected
     */
    @Prop() readonly min: number = 0;

    /**
     * The maximum value that can be selected
     */
    @Prop() readonly max: number = 10;

    /**
     * The default value to give to the input, it defaults to the min value
     */
    @Prop() readonly value: number = this.min;

    /**
     * The step to give the range input element
     */
    @Prop() readonly step: number | "any" = 1;

    /**
     * The label of the component, can also be inserted per slot
     */
    @Prop() readonly label: string = "";

    /**
     * Additional info label for the input field
     */
    @Prop() readonly labelInfo: string = "";

    /**
     * The name to be assigned to the range input
     */
    // @ts-expect-error Check for typing
    @Prop() readonly name: string = undefined;

    /**
     * If set, the component will be displayed in error mode
     */
    @Prop({ mutable: true, reflect: true }) error = false;

    /**
     * The error message to be displayed to the user
     */
    @Prop() readonly errorMsg: string = "Invalid value";

    /**
     * If set, the input will be disabled
     */
    @Prop() readonly disabled: boolean = false;

    /**
     * ID attribute for the input element, this will be set automatically if it is not set by the developer
     */
    /* eslint-disable-next-line */
    @Prop() inputId: string = "";

    @State() hasSlottedLabel = false;
    @State() markerOffsetX = 0;

    /**
     * Range input event emitted on change, includes value, inputId, and name
     */
    @Event({
        eventName: "uxRangeInputChange",
        composed: true,
        cancelable: true,
        bubbles: true
    })
    rangeInputEvent!: EventEmitter<{
        value: string;
        inputId: string;
        name: string;
    }>;

    componentWillLoad() {
        if (this.inputId === "") {
            this.inputId = `${this.baseClass}-id-${id++}`;
        }
        const firstLevelChildren = this.element.children;
        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let i = 0; i < firstLevelChildren.length; i++) {
            const slotAttribute = firstLevelChildren[i].getAttribute("slot");
            if (slotAttribute === "info-min" || slotAttribute === "info-max") {
                return;
            }
            this.hasSlottedLabel = true;
        }
    }

    @Listen("resize", { target: "window" })
    onResize() {
        this.recalculateMarkerPosition();
    }

    private recalculateMarkerPosition = debounce(() => this.getMarkerPosition(), 100);

    componentDidLoad() {
        this.getMarkerPosition();
    }

    private onChange = () => {
        this.error = false;
        this.rangeInputEvent.emit({
            value: this.rangeInput.value,
            inputId: this.inputId,
            name: this.name
        });
    };

    private getMarkerPosition = () => {
        let offsetReference = (100 / this.max - this.min) * (parseFloat(this.rangeInput.value) - this.min);
        if (offsetReference < 0) {
            offsetReference = 0;
        } else if (offsetReference > 100) {
            offsetReference = 100;
        }
        this.markerOffsetX =
            (this.rangeInput.getBoundingClientRect().width - this.rangeMarker.clientWidth) * (offsetReference / 100);
    };

    private onInvalid = () => {
        this.error = true;
    };

    render() {
        return (
            <Host
                class={{
                    [this.baseClass]: true,
                    [`${this.baseClass}--disabled`]: this.disabled,
                    [`${this.baseClass}--error`]: this.error
                }}
            >
                {(this.label !== "" || this.hasSlottedLabel) && (
                    <Label
                        label={this.label}
                        error={this.error}
                        errorMsg={this.errorMsg}
                        htmlFor={this.inputId}
                        labelInfo={this.labelInfo}
                    >
                        <slot />
                    </Label>
                )}
                <div class={`${this.baseClass}__track`}>
                    <input
                        type="range"
                        name={this.name}
                        min={this.min}
                        max={this.max}
                        step={this.step}
                        id={this.inputId}
                        value={this.value}
                        onChange={this.onChange}
                        onInput={this.getMarkerPosition}
                        onInvalid={this.onInvalid}
                        // @ts-expect-error Check for typing
                        ref={(el) => (this.rangeInput = el)}
                        class={`${this.baseClass}__input`}
                        aria-invalid={this.error ? "true" : "false"}
                    />
                    <div
                        class={`${this.baseClass}__marker`}
                        style={{
                            transform: `translateX(${this.markerOffsetX}px)`
                        }}
                        // @ts-expect-error Check for typing
                        ref={(el) => (this.rangeMarker = el)}
                    >
                        <div class={`${this.baseClass}__marker-line`}></div>
                        <div class={`${this.baseClass}__marker-line`}></div>
                        <div class={`${this.baseClass}__marker-line`}></div>
                    </div>
                </div>
                <div class={`${this.baseClass}__info-container`}>
                    <div class={`${this.baseClass}__info`}>
                        <slot name="info-min"></slot>
                    </div>
                    <div class={`${this.baseClass}__info`}>
                        <slot name="info-max"></slot>
                    </div>
                </div>
            </Host>
        );
    }
}
